import React, { useMemo, useState, useDeferredValue, useEffect, useRef } from "react";
import Footer from "./footer.jsx";
import STATS from "../statsData.json";
import SAMPLE_QUESTION_BANK from "../sampleQuestions.json";
import { useMeta } from "../seo.js";
import { ROUTE_META } from "../routeMeta.js";

const TABS = [
  { id: "ask", label: "Ask", count: null },
  { id: "overview", label: "Stats", count: null },
  { id: "pitchers", label: "Pitchers", count: STATS.pitchers.length },
  { id: "teams", label: "Teams", count: STATS.teams.length },
  { id: "career", label: "Splits", count: null },
];

// Pick N random items from a list (Fisher-Yates partial shuffle).
function pickRandom(arr, n) {
  const out = arr.slice();
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (out.length - i));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.slice(0, n);
}

const SAMPLE_QUESTION_COUNT = 9;

const PITCHER_COLS = [
  { key: "AB", label: "AB" },
  { key: "H", label: "H" },
  { key: "2B", label: "2B" },
  { key: "3B", label: "3B" },
  { key: "HR", label: "HR" },
  { key: "BB", label: "BB" },
  { key: "SO", label: "SO" },
  { key: "AVG", label: "AVG" },
  { key: "OBP", label: "OBP" },
  { key: "SLG", label: "SLG" },
];

const TEAM_COLS = [
  { key: "G", label: "G" },
  { key: "AB", label: "AB" },
  { key: "R", label: "R" },
  { key: "H", label: "H" },
  { key: "2B", label: "2B" },
  { key: "3B", label: "3B" },
  { key: "HR", label: "HR" },
  { key: "RBI", label: "RBI" },
  { key: "BB", label: "BB" },
  { key: "SO", label: "SO" },
  { key: "SB", label: "SB" },
  { key: "AVG", label: "AVG" },
  { key: "OBP", label: "OBP" },
  { key: "SLG", label: "SLG" },
];

// Default order: pitchers Greg hit best against. Sort by AVG descending,
// with a larger sample (AB) winning ties — a .500 in 4 AB still beats a
// .400 in 5 AB, but a 4-AB and 5-AB tie at .500 puts the 5-AB pitcher first.
const PITCHERS_SORTED = [...STATS.pitchers].sort((a, b) => {
  const av = parseFloat(a.AVG);
  const bv = parseFloat(b.AVG);
  if (bv !== av) return bv - av;
  return b.AB - a.AB;
});

function filterPitchers(query) {
  if (!query) return PITCHERS_SORTED;
  const q = query.toLowerCase();
  return PITCHERS_SORTED.filter((p) => p.name.toLowerCase().includes(q));
}

function filterTeams(query) {
  if (!query) return STATS.teams;
  const q = query.toLowerCase();
  return STATS.teams.filter(
    (t) => t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q),
  );
}

export default function StatsPage() {
  useMeta(ROUTE_META.stats);
  return (
    <>
      <main className="stats-page grain">
        <header className="stats-nav">
          <a href="/" className="stats-brand">
            <span className="stats-brand-mark">GP</span>
            <span>Greg Pryor</span>
          </a>
          <a className="stats-home-link mono" href="/">
            Back to the site
          </a>
        </header>

        <section className="stats-hero">
          <div className="stats-hero-inner">
            <div>
              <div className="stats-kicker mono">Career Statistics</div>
              <h1 className="display stats-title">Every pitcher I faced.</h1>
              <p className="stats-lede">
                Ten big-league seasons, 1,883 at-bats, 471 hits. Search any pitcher I batted against
                at least four times, or any opponent club, and see exactly how I did.
              </p>
            </div>
            <div className="stats-totals">
              <CareerStat label="Games" value={STATS.career.G} />
              <CareerStat label="At Bats" value={STATS.career.AB.toLocaleString()} />
              <CareerStat label="Hits" value={STATS.career.H} />
              <CareerStat label="HR" value={STATS.career.HR} />
              <CareerStat label="RBI" value={STATS.career.RBI} />
              <CareerStat label="AVG" value={STATS.career.AVG} mono />
              <CareerStat label="OBP" value={STATS.career.OBP} mono />
              <CareerStat label="SLG" value={STATS.career.SLG} mono />
            </div>
          </div>
        </section>

        <StatsExplorer />
      </main>
      <Footer />
    </>
  );
}

// Inner stats experience — used by both /stats (full page) and the Career
// section on the home page. Pass `embedded` to drop sticky behavior and
// trim padding so it sits cleanly inside another section.
export function StatsExplorer({ embedded = false }) {
  const [tab, setTab] = useState("ask");
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);

  const filteredPitchers = useMemo(() => filterPitchers(deferredQuery), [deferredQuery]);
  const filteredTeams = useMemo(() => filterTeams(deferredQuery), [deferredQuery]);

  const showSearch = tab === "pitchers" || tab === "teams";
  const placeholder =
    tab === "teams"
      ? "Search a team — Yankees, Brewers, Tigers…"
      : "Search a pitcher — Ryan, Guidry, Stieb…";

  const rootClass = `stats-explorer ${embedded ? "is-embedded" : ""}`;
  return (
    <div className={rootClass}>
      <section className="stats-controls">
        <div className="stats-controls-inner">
          <div className="stats-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={`stats-tab ${tab === t.id ? "is-active" : ""}`}
                onClick={() => {
                  setTab(t.id);
                  setQuery("");
                }}
              >
                <span>{t.label}</span>
                {t.count != null && <span className="stats-tab-count mono">{t.count}</span>}
              </button>
            ))}
          </div>
          {showSearch && (
            <div className="stats-search">
              <svg className="stats-search-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
                <line
                  x1="16.5"
                  y1="16.5"
                  x2="21"
                  y2="21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={placeholder}
                aria-label="Search"
                autoComplete="off"
                spellCheck="false"
              />
              {query && (
                <button
                  className="stats-search-clear"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="stats-results">
        <div className="stats-results-inner">
          {tab === "ask" && <AskView />}
          {tab === "overview" && <CareerOverview />}
          {tab === "pitchers" && <PitcherResults rows={filteredPitchers} query={deferredQuery} />}
          {tab === "teams" && <TeamResults rows={filteredTeams} query={deferredQuery} />}
          {tab === "career" && <SplitsView />}
        </div>
      </section>
    </div>
  );
}

// Reusable copy for the "About the data" modal. Exported so the footer
// modal can render the same text without duplicating it.
export const ABOUT_THE_DATA_TEXT = (
  <p>
    All numbers come straight from{" "}
    <a href="https://www.retrosheet.org/" target="_blank" rel="noopener noreferrer">
      Retrosheet
    </a>
    's play-by-play event files for the 1976-1986 seasons. Career batting and fielding totals are
    sanity-checked against official records — 1,883 AB, 471 H, 14 HR, 789 games all match exactly.
    Per-pitcher matchups include every pitcher Greg faced at least four times (Retrosheet's standard
    "selected matchups" cutoff); pitchers he saw fewer times aren't in that table.
    Fielding-by-batter numbers count every batted ball Greg fielded off a hitter (hits included),
    not just putouts. The natural-language Ask tab is powered by Anthropic's Claude API — questions
    are answered from the dataset above, not the open internet.
  </p>
);

function CareerStat({ label, value, mono }) {
  return (
    <div className="stats-total">
      <div className="stats-total-label mono">{label}</div>
      <div className={`stats-total-value ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  );
}

// The "Stats" tab: Greg's overall career line (counting totals + slash) plus a
// year-by-year batting table with a Career totals row. Reuses the same
// totals grid as the /stats hero and the same table/card pattern as Teams,
// so it inherits the existing desktop-table / mobile-card responsive switch.
function CareerOverview() {
  const c = STATS.career;
  return (
    <div className="stats-overview">
      <div className="stats-result-meta mono">
        Ten seasons · 1976–1986 · {c.AVG} / {c.OBP} / {c.SLG}
      </div>
      <div className="stats-totals">
        <CareerStat label="Games" value={c.G} />
        <CareerStat label="At Bats" value={c.AB.toLocaleString()} />
        <CareerStat label="Runs" value={c.R} />
        <CareerStat label="Hits" value={c.H} />
        <CareerStat label="Doubles" value={c["2B"]} />
        <CareerStat label="Triples" value={c["3B"]} />
        <CareerStat label="Home Runs" value={c.HR} />
        <CareerStat label="RBI" value={c.RBI} />
        <CareerStat label="Stolen Bases" value={c.SB} />
        <CareerStat label="AVG" value={c.AVG} mono />
        <CareerStat label="OBP" value={c.OBP} mono />
        <CareerStat label="SLG" value={c.SLG} mono />
      </div>

      <div className="stats-overview-byyear">
        <div className="stats-result-meta mono">Year by year</div>
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr>
                <th className="stats-th-name">Season</th>
                {TEAM_COLS.map((col) => (
                  <th key={col.key} className="stats-th-num">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {STATS.byYear.map((y) => (
                <tr key={y.year}>
                  <td className="stats-td-name">
                    {y.year}
                    <span className="stats-td-sub mono">{y.teamCode}</span>
                  </td>
                  {TEAM_COLS.map((col) => (
                    <td key={col.key} className="stats-td-num mono">
                      {y[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="stats-tr-total">
                <td className="stats-td-name">
                  Career
                  <span className="stats-td-sub mono">10 yrs</span>
                </td>
                {TEAM_COLS.map((col) => (
                  <td key={col.key} className="stats-td-num mono">
                    {c[col.key]}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="stats-cards">
          {STATS.byYear.map((y) => (
            <article key={y.year} className="stats-card">
              <div className="stats-card-head">
                <div className="stats-card-title">{y.year}</div>
                <div className="stats-card-tag mono">
                  {y.teamCode} · {y.G} G
                </div>
              </div>
              <div className="stats-card-line mono">
                {y.AB} AB · {y.H} H · {y.HR} HR · {y.RBI} RBI · {y.BB} BB · {y.SO} SO
              </div>
              <div className="stats-card-slash mono">
                <Slash avg={y.AVG} obp={y.OBP} slg={y.SLG} />
              </div>
            </article>
          ))}
          <article className="stats-card stats-card-total">
            <div className="stats-card-head">
              <div className="stats-card-title">Career</div>
              <div className="stats-card-tag mono">10 yrs · {c.G} G</div>
            </div>
            <div className="stats-card-line mono">
              {c.AB} AB · {c.H} H · {c.HR} HR · {c.RBI} RBI · {c.BB} BB · {c.SO} SO
            </div>
            <div className="stats-card-slash mono">
              <Slash avg={c.AVG} obp={c.OBP} slg={c.SLG} />
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

const PITCHER_INITIAL = 15;

function PitcherResults({ rows, query }) {
  const [showAll, setShowAll] = useState(false);

  // Reset the collapsed view when the user types a new search — they want to
  // see the matches, not 15 of the matches.
  React.useEffect(() => {
    setShowAll(false);
  }, [query]);

  if (rows.length === 0) {
    return <EmptyState query={query} kind="pitcher" />;
  }

  const isSearching = query.length > 0;
  const collapsed = !showAll && !isSearching && rows.length > PITCHER_INITIAL;
  const visible = collapsed ? rows.slice(0, PITCHER_INITIAL) : rows;
  const hiddenCount = rows.length - visible.length;

  return (
    <>
      <div className="stats-result-meta mono">
        {collapsed
          ? `Showing ${visible.length} of ${rows.length} pitchers`
          : `Showing ${rows.length} ${rows.length === 1 ? "pitcher" : "pitchers"}`}
      </div>
      <div className="stats-table-wrap">
        <table className="stats-table">
          <thead>
            <tr>
              <th className="stats-th-name">Pitcher</th>
              <th className="stats-th-hand">Throws</th>
              {PITCHER_COLS.map((c) => (
                <th key={c.key} className="stats-th-num">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.name}>
                <td className="stats-td-name">{p.name}</td>
                <td className="stats-td-hand mono">{p.hand}HP</td>
                {PITCHER_COLS.map((c) => (
                  <td key={c.key} className="stats-td-num mono">
                    {p[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="stats-cards">
        {visible.map((p) => (
          <article key={p.name} className="stats-card">
            <div className="stats-card-head">
              <div className="stats-card-title">{p.name}</div>
              <div className="stats-card-tag mono">{p.hand === "L" ? "LHP" : "RHP"}</div>
            </div>
            <div className="stats-card-line mono">
              {p.AB} AB · {p.H} H · {p.HR} HR · {p.BB} BB · {p.SO} SO
            </div>
            <div className="stats-card-slash mono">
              <Slash avg={p.AVG} obp={p.OBP} slg={p.SLG} />
            </div>
          </article>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button className="stats-see-more" onClick={() => setShowAll(true)}>
          See {hiddenCount} more {hiddenCount === 1 ? "pitcher" : "pitchers"}
        </button>
      )}
      {showAll && rows.length > PITCHER_INITIAL && !isSearching && (
        <button className="stats-see-more stats-see-less" onClick={() => setShowAll(false)}>
          Collapse to top 15
        </button>
      )}
    </>
  );
}

function TeamResults({ rows, query }) {
  if (rows.length === 0) {
    return <EmptyState query={query} kind="team" />;
  }
  return (
    <>
      <div className="stats-result-meta mono">
        Showing {rows.length} {rows.length === 1 ? "team" : "teams"}
      </div>
      <div className="stats-table-wrap">
        <table className="stats-table">
          <thead>
            <tr>
              <th className="stats-th-name">Opponent</th>
              {TEAM_COLS.map((c) => (
                <th key={c.key} className="stats-th-num">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.code}>
                <td className="stats-td-name">
                  {t.name}
                  <span className="stats-td-sub mono">{t.code}</span>
                </td>
                {TEAM_COLS.map((c) => (
                  <td key={c.key} className="stats-td-num mono">
                    {t[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="stats-cards">
        {rows.map((t) => (
          <article key={t.code} className="stats-card">
            <div className="stats-card-head">
              <div className="stats-card-title">{t.name}</div>
              <div className="stats-card-tag mono">{t.G} G</div>
            </div>
            <div className="stats-card-line mono">
              {t.AB} AB · {t.H} H · {t.HR} HR · {t.RBI} RBI · {t.BB} BB · {t.SO} SO
            </div>
            <div className="stats-card-slash mono">
              <Slash avg={t.AVG} obp={t.OBP} slg={t.SLG} />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function Slash({ avg, obp, slg }) {
  return (
    <>
      <span className="stats-slash-num">{avg}</span>
      <span className="stats-slash-sep">/</span>
      <span className="stats-slash-num">{obp}</span>
      <span className="stats-slash-sep">/</span>
      <span className="stats-slash-num">{slg}</span>
    </>
  );
}

function EmptyState({ query, kind }) {
  return (
    <div className="stats-empty">
      <div className="stats-empty-mark mono">No match</div>
      <p>
        Nothing in the {kind} list for <strong>"{query}"</strong>. Try a last name only, or check
        the spelling — these were ballplayers in the late '70s and early '80s, the names aren't
        always intuitive.
      </p>
    </div>
  );
}

function AskView() {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | error
  // The 9 sample suggestions stay visible as the user keeps asking, so they
  // can keep tapping new ones without backing out. Shuffle reshuffles them.
  const [shuffleKey, setShuffleKey] = useState(0);
  const samples = useMemo(
    () => pickRandom(SAMPLE_QUESTION_BANK, SAMPLE_QUESTION_COUNT),
    [shuffleKey],
  );
  // Track which samples the user has already asked, so they're styled as
  // "used" but stay visible and clickable.
  const askedSet = useMemo(
    () => new Set(history.filter((t) => t.role === "user").map((t) => t.text)),
    [history],
  );
  // After each new turn arrives, scroll it into view so mobile users see the
  // question they just asked (and then the answer when it streams in) instead
  // of staring at the unchanged input row at the bottom of the page.
  const threadRef = useRef(null);
  const lastTurnRef = useRef(null);
  useEffect(() => {
    if (!history.length) return;
    const el = lastTurnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = window.scrollY + rect.top - 80;
    window.scrollTo({ top, behavior: "smooth" });
  }, [history.length, status]);

  const ask = async (q) => {
    const text = q.trim();
    if (!text || status === "loading") return;
    setStatus("loading");
    setQuestion("");
    setHistory((h) => [...h, { role: "user", text }]);
    try {
      const res = await fetch("/api/stats-ask", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ question: text }),
      });
      const data = await res.json();
      if (!res.ok) {
        setHistory((h) => [...h, { role: "error", text: data.error || "Something went wrong." }]);
      } else {
        setHistory((h) => [...h, { role: "answer", text: data.answer }]);
      }
      setStatus("idle");
    } catch {
      setHistory((h) => [
        ...h,
        { role: "error", text: "Couldn't reach the answer service. Check your connection?" },
      ]);
      setStatus("idle");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask(question);
  };

  return (
    <div className="stats-ask">
      {history.length === 0 && (
        <div className="stats-ask-intro">
          <h2 className="display stats-ask-h">Ask anything about my career.</h2>
          <p>
            Powered by my full Retrosheet splits — career pitcher matchups, team-by-team totals,
            year-by-year batting, and situational splits. Try one of these or write your own.
          </p>
        </div>
      )}

      {history.length > 0 && (
        <div className="stats-ask-thread" ref={threadRef}>
          {history.map((turn, i) => {
            const isLast = i === history.length - 1;
            return (
              <div
                key={i}
                ref={isLast && status !== "loading" ? lastTurnRef : null}
                className={`stats-ask-turn stats-ask-${turn.role}`}
              >
                {turn.role === "user" ? (
                  <>
                    <div className="stats-ask-role mono">You asked</div>
                    <div className="stats-ask-text">{turn.text}</div>
                  </>
                ) : turn.role === "error" ? (
                  <>
                    <div className="stats-ask-role mono">Hm —</div>
                    <div className="stats-ask-text">{turn.text}</div>
                  </>
                ) : (
                  <>
                    <div className="stats-ask-role mono">Greg says</div>
                    <AnswerBody text={turn.text} />
                  </>
                )}
              </div>
            );
          })}
          {status === "loading" && (
            <div className="stats-ask-turn stats-ask-loading" ref={lastTurnRef}>
              <div className="stats-ask-role mono">Pulling the splits…</div>
              <div className="stats-ask-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
          <div className="stats-ask-threadbar">
            <button
              type="button"
              className="stats-ask-back"
              onClick={() => {
                setHistory([]);
                setQuestion("");
                setShuffleKey((k) => k + 1);
              }}
              disabled={status === "loading"}
            >
              <span aria-hidden="true">↺</span> Clear and start over
            </button>
          </div>
        </div>
      )}

      <div className="stats-ask-samples-block">
        <div className="stats-ask-samples-header">
          <span className="stats-ask-samples-label mono">
            {history.length > 0 ? "Try another" : "Try one of these"}
          </span>
          <button
            type="button"
            className="stats-ask-shuffle"
            onClick={() => setShuffleKey((k) => k + 1)}
            aria-label="Shuffle to 9 new suggested questions"
          >
            <span className="stats-ask-shuffle-icon" aria-hidden="true">
              ↻
            </span>
            <span>Shuffle</span>
          </button>
        </div>
        <div className="stats-ask-samples">
          {samples.map((s) => {
            const used = askedSet.has(s);
            return (
              <button
                key={s}
                className={`stats-ask-sample${used ? " is-used" : ""}`}
                onClick={() => ask(s)}
                disabled={status === "loading"}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <form className="stats-ask-form" onSubmit={onSubmit}>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              ask(question);
            }
          }}
          placeholder="Ask a question — e.g. who hit Greg hardest? Who did he homer off?"
          rows={2}
          aria-label="Ask a question"
          disabled={status === "loading"}
        />
        <button type="submit" disabled={!question.trim() || status === "loading"}>
          {status === "loading" ? "Asking…" : "Ask"}
        </button>
      </form>
      <p className="stats-ask-fineprint mono">
        Answers are generated from the dataset above by an AI model. Numbers come from Retrosheet —
        any narrative around them is the model's interpretation.
      </p>
    </div>
  );
}

function AnswerBody({ text }) {
  // Render plain text with paragraph breaks and basic **bold** support.
  const blocks = text.split(/\n\n+/);
  return (
    <div className="stats-ask-text">
      {blocks.map((block, i) => {
        const isList = /^\s*[-•]\s/m.test(block);
        if (isList) {
          const items = block
            .split(/\n/)
            .map((line) => line.replace(/^\s*[-•]\s*/, "").trim())
            .filter(Boolean);
          return (
            <ul key={i} className="stats-ask-list">
              {items.map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ul>
          );
        }
        return <p key={i}>{renderInline(block)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  // Split on **bold** segments and emit <strong> for them.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function SplitsView() {
  const s = STATS.situational;
  const rows = [
    { label: "vs RHP", k: "vsRHP" },
    { label: "vs LHP", k: "vsLHP" },
    { label: "Home", k: "home" },
    { label: "Away", k: "away" },
    { label: "Day games", k: "day" },
    { label: "Night games", k: "night" },
    { label: "RISP", k: "risp" },
    { label: "Close & Late", k: "closeAndLate" },
    { label: "Bases Loaded", k: "basesLoaded" },
  ];
  return (
    <div className="stats-splits">
      {rows.map((r) => {
        const d = s[r.k];
        return (
          <article key={r.k} className="stats-split-card">
            <header>
              <div className="stats-split-label">{r.label}</div>
              <div className="stats-split-slash mono">
                <Slash avg={d.AVG} obp={d.OBP} slg={d.SLG} />
              </div>
            </header>
            <dl>
              {d.G != null && (
                <div>
                  <dt>G</dt>
                  <dd className="mono">{d.G}</dd>
                </div>
              )}
              <div>
                <dt>AB</dt>
                <dd className="mono">{d.AB}</dd>
              </div>
              <div>
                <dt>H</dt>
                <dd className="mono">{d.H}</dd>
              </div>
              <div>
                <dt>HR</dt>
                <dd className="mono">{d.HR}</dd>
              </div>
              <div>
                <dt>RBI</dt>
                <dd className="mono">{d.RBI}</dd>
              </div>
              {d.BB != null && (
                <div>
                  <dt>BB</dt>
                  <dd className="mono">{d.BB}</dd>
                </div>
              )}
              {d.SO != null && (
                <div>
                  <dt>SO</dt>
                  <dd className="mono">{d.SO}</dd>
                </div>
              )}
            </dl>
          </article>
        );
      })}
    </div>
  );
}

const statsStyles = document.createElement("style");
statsStyles.textContent = `
.stats-page {
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(0, 70, 135, 0.24), rgba(5, 13, 26, 0) 520px),
    var(--navy-900);
  color: var(--bone);
}
.stats-nav {
  position: sticky;
  top: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 32px;
  background: rgba(5, 13, 26, 0.82);
  border-bottom: 1px solid var(--rule);
  backdrop-filter: blur(14px) saturate(140%);
}
.stats-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--serif);
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--bone);
}
.stats-brand-mark {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 1px solid var(--royal-blue-bright);
  border-radius: 50%;
  background: var(--royal-blue);
  font-family: var(--mono);
  font-size: 0.6875rem;
}
.stats-home-link {
  color: var(--gold);
  font-size: 0.6875rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.stats-hero { padding: 64px 0 40px; }
.stats-hero-inner,
.stats-controls-inner,
.stats-results-inner,
.stats-source-inner {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 56px;
}
.stats-hero-inner {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 56px;
  align-items: end;
}
.stats-kicker {
  color: var(--gold);
  font-size: 0.6875rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 16px;
}
.stats-title {
  margin: 0 0 18px;
  font-size: clamp(2.5rem, 7vw, 5.75rem);
  line-height: 0.98;
  color: var(--bone);
  max-width: 16ch;
}
.stats-lede {
  margin: 0;
  max-width: 56ch;
  color: var(--bone-dim);
  font-family: var(--serif);
  font-size: clamp(1rem, 1.4vw, 1.1875rem);
  line-height: 1.55;
}

.stats-totals {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--rule);
  border: 1px solid var(--rule);
}
.stats-total {
  padding: 14px 16px;
  background: rgba(3, 8, 16, 0.78);
}
.stats-total-label {
  color: var(--gold);
  font-size: 0.5625rem;
  letter-spacing: 0.18em;
  margin-bottom: 6px;
}
.stats-total-value {
  font-family: var(--serif);
  font-size: 1.375rem;
  color: var(--bone);
  line-height: 1;
}
.stats-total-value.mono {
  font-family: var(--mono);
  font-size: 1.125rem;
}

.stats-controls {
  position: sticky;
  top: 68px;
  z-index: 50;
  background: rgba(5, 13, 26, 0.88);
  backdrop-filter: blur(12px) saturate(140%);
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  padding: 18px 0;
}
/* Embedded mode: dropped into a host section (home page). No sticky, no
   page-edge backdrop, no full-bleed bars — sit cleanly inside the section. */
.stats-explorer.is-embedded .stats-controls {
  position: static;
  background: transparent;
  backdrop-filter: none;
  border-top: 0;
  border-bottom: 1px solid var(--rule);
  padding: 0 0 18px;
  margin-bottom: 18px;
}
.stats-explorer.is-embedded .stats-controls-inner,
.stats-explorer.is-embedded .stats-results-inner,
.stats-explorer.is-embedded .stats-source-inner {
  max-width: none;
  padding: 0;
}
.stats-explorer.is-embedded .stats-results { padding: 0; }
.stats-controls-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
}
.stats-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.6);
  border-radius: 999px;
}
.stats-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--bone-dim);
  font-family: var(--sans);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 200ms, background 200ms;
}
.stats-tab:hover { color: var(--bone); }
.stats-tab.is-active {
  background: var(--royal-blue);
  color: var(--bone);
}
.stats-tab-count {
  font-size: 0.625rem;
  opacity: 0.7;
}
.stats-tab.is-active .stats-tab-count { opacity: 0.9; }

.stats-search {
  position: relative;
  display: flex;
  align-items: center;
}
.stats-search input {
  width: 100%;
  padding: 12px 38px 12px 42px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.7);
  color: var(--bone);
  font-family: var(--sans);
  font-size: 0.9375rem;
  border-radius: 8px;
  -webkit-appearance: none;
  appearance: none;
}
.stats-search input:focus {
  outline: none;
  border-color: var(--royal-blue-bright);
  box-shadow: 0 0 0 3px rgba(0, 100, 194, 0.18);
}
.stats-search input::placeholder { color: rgba(232, 227, 214, 0.42); }
.stats-search-icon {
  position: absolute;
  left: 14px;
  width: 18px;
  height: 18px;
  color: rgba(232, 227, 214, 0.5);
  pointer-events: none;
}
.stats-search-clear {
  position: absolute;
  right: 8px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: rgba(189, 155, 96, 0.18);
  color: var(--bone);
  font-size: 1.125rem;
  line-height: 1;
  cursor: pointer;
}
.stats-search-clear:hover { background: rgba(189, 155, 96, 0.32); }

.stats-results { padding: 40px 0 80px; }
.stats-result-meta {
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.18em;
  margin-bottom: 18px;
  text-transform: uppercase;
}

.stats-see-more {
  display: block;
  margin: 18px auto 0;
  padding: 12px 24px;
  border: 1px solid var(--rule);
  border-radius: 999px;
  background: transparent;
  color: var(--bone-dim);
  font-family: var(--sans);
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: color 200ms, border-color 200ms, background 200ms;
}
.stats-see-more:hover {
  color: var(--gold);
  border-color: var(--gold);
  background: rgba(189, 155, 96, 0.06);
}
.stats-see-less {
  color: rgba(232, 227, 214, 0.55);
  font-size: 0.75rem;
}

.stats-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.55);
}
.stats-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--mono);
  font-size: 0.8125rem;
}
.stats-table thead th {
  position: sticky;
  top: 0;
  background: rgba(0, 70, 135, 0.4);
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 12px 10px;
  text-align: right;
  font-weight: 600;
  border-bottom: 1px solid var(--rule);
}
.stats-table thead th.stats-th-name,
.stats-table thead th.stats-th-hand { text-align: left; }
.stats-table tbody tr:nth-child(even) { background: rgba(255, 255, 255, 0.018); }
.stats-table tbody tr:hover { background: rgba(0, 100, 194, 0.1); }
.stats-table tbody td {
  padding: 11px 10px;
  border-bottom: 1px solid rgba(189, 155, 96, 0.08);
  text-align: right;
  color: var(--bone);
  white-space: nowrap;
}
.stats-table tbody td.stats-td-name {
  text-align: left;
  font-family: var(--serif);
  font-size: 1rem;
  color: var(--bone);
  min-width: 180px;
}
.stats-table tbody td.stats-td-hand { text-align: left; color: var(--bone-dim); font-size: 0.6875rem; }
.stats-td-sub {
  display: inline-block;
  margin-left: 10px;
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.14em;
}

.stats-cards { display: none; }

.stats-empty {
  padding: 40px 24px;
  border: 1px dashed var(--rule);
  text-align: center;
  background: rgba(3, 8, 16, 0.4);
}
.stats-empty-mark {
  display: inline-block;
  margin-bottom: 14px;
  padding: 4px 10px;
  border: 1px solid var(--rule);
  border-radius: 999px;
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.18em;
}
.stats-empty p {
  margin: 0;
  color: var(--bone-dim);
  font-family: var(--serif);
  font-size: 1rem;
  max-width: 50ch;
  margin-inline: auto;
  line-height: 1.5;
}
.stats-empty strong { color: var(--bone); }

.stats-splits {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.stats-split-card {
  padding: 18px 20px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.55);
}
.stats-split-card header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--rule);
}
.stats-split-label {
  font-family: var(--serif);
  font-size: 1.1875rem;
  color: var(--bone);
}
.stats-split-slash {
  font-size: 0.8125rem;
  color: var(--bone-dim);
}
.stats-split-card dl {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 8px;
  margin: 0;
}
.stats-split-card dl > div { display: grid; gap: 2px; }
.stats-split-card dt {
  color: var(--gold);
  font-size: 0.5625rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-family: var(--mono);
}
.stats-split-card dd {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--bone);
}

.stats-slash-sep { color: var(--gold); margin: 0 4px; }
.stats-slash-num { color: var(--bone); }

.stats-ask {
  max-width: 820px;
  margin: 0 auto;
}
.stats-ask-intro h2 {
  margin: 0 0 16px;
  font-size: clamp(1.75rem, 3.4vw, 2.75rem);
  color: var(--bone);
}
.stats-ask-intro > p {
  margin: 0 0 24px;
  color: var(--bone-dim);
  font-family: var(--serif);
  font-size: 1.0625rem;
  line-height: 1.55;
}
.stats-ask-samples-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--rule);
}
.stats-ask-samples-label {
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--bone-dim);
}
.stats-ask-shuffle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.55);
  color: var(--bone-dim);
  font-family: var(--mono);
  font-size: 0.71875rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 200ms, color 200ms, background 200ms;
  -webkit-tap-highlight-color: transparent;
}
.stats-ask-shuffle-icon {
  display: inline-block;
  font-size: 0.875rem;
  line-height: 1;
  transition: transform 400ms cubic-bezier(0.34, 1.3, 0.5, 1);
}
@media (hover: hover) and (pointer: fine) {
  .stats-ask-shuffle:hover {
    border-color: var(--royal-blue-bright);
    background: rgba(0, 100, 194, 0.08);
    color: var(--bone);
  }
  .stats-ask-shuffle:hover .stats-ask-shuffle-icon {
    transform: rotate(180deg);
  }
}
.stats-ask-shuffle:active .stats-ask-shuffle-icon {
  transform: rotate(180deg);
}
.stats-ask-samples-block {
  margin-bottom: 28px;
}
.stats-ask-samples {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
}
.stats-ask-sample {
  text-align: left;
  padding: 14px 16px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.55);
  color: var(--bone-dim);
  font-family: var(--serif);
  font-size: 0.90625rem;
  line-height: 1.35;
  cursor: pointer;
  transition: border-color 200ms, color 200ms, background 200ms, opacity 200ms;
  -webkit-tap-highlight-color: transparent;
}
.stats-ask-sample.is-used {
  opacity: 0.45;
  border-style: dashed;
}
.stats-ask-sample:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
@media (hover: hover) and (pointer: fine) {
  .stats-ask-sample:hover:not(:disabled) {
    border-color: var(--royal-blue-bright);
    background: rgba(0, 100, 194, 0.08);
    color: var(--bone);
    opacity: 1;
  }
}
/* On touch: only apply the highlight on actual press, not on scroll-touch. */
.stats-ask-sample:active:not(:disabled) {
  border-color: var(--royal-blue-bright);
  background: rgba(0, 100, 194, 0.12);
  color: var(--bone);
}
.stats-ask-thread {
  display: grid;
  gap: 22px;
  margin-bottom: 22px;
}
.stats-ask-threadbar {
  display: flex;
  justify-content: flex-start;
}
.stats-ask-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid var(--rule);
  border-radius: 999px;
  background: transparent;
  color: var(--bone-dim);
  font-family: var(--sans);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: color 200ms, border-color 200ms, background 200ms;
}
.stats-ask-back:hover:not(:disabled) {
  color: var(--gold);
  border-color: var(--gold);
  background: rgba(189, 155, 96, 0.06);
}
.stats-ask-back:disabled { opacity: 0.4; cursor: not-allowed; }
.stats-ask-back span { font-size: 0.875rem; line-height: 1; }
.stats-ask-turn {
  padding: 18px 20px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.55);
}
.stats-ask-user { background: rgba(0, 70, 135, 0.12); }
.stats-ask-error { border-color: rgba(200, 16, 46, 0.4); background: rgba(200, 16, 46, 0.06); }
.stats-ask-role {
  color: var(--gold);
  font-size: 0.625rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.stats-ask-user .stats-ask-role { color: var(--royal-blue-glow); }
.stats-ask-error .stats-ask-role { color: var(--crimson); }
.stats-ask-text {
  color: var(--bone);
  font-family: var(--serif);
  font-size: 1.0625rem;
  line-height: 1.55;
}
.stats-ask-text p { margin: 0 0 10px; }
.stats-ask-text p:last-child { margin-bottom: 0; }
.stats-ask-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
}
.stats-ask-list li {
  padding-left: 18px;
  position: relative;
}
.stats-ask-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 11px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gold);
}
.stats-ask-text strong { color: var(--bone); font-weight: 600; }
.stats-ask-dots {
  display: inline-flex;
  gap: 6px;
}
.stats-ask-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gold);
  animation: stats-ask-pulse 1.2s infinite ease-in-out;
}
.stats-ask-dots span:nth-child(2) { animation-delay: 0.15s; }
.stats-ask-dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes stats-ask-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}

.stats-ask-form {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 14px;
}
.stats-ask-form textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.7);
  color: var(--bone);
  font-family: var(--serif);
  font-size: 1rem;
  line-height: 1.4;
  border-radius: 8px;
  resize: vertical;
  min-height: 56px;
  -webkit-appearance: none;
  appearance: none;
}
.stats-ask-form textarea:focus {
  outline: none;
  border-color: var(--royal-blue-bright);
  box-shadow: 0 0 0 3px rgba(0, 100, 194, 0.18);
}
.stats-ask-form textarea::placeholder { color: rgba(232, 227, 214, 0.4); }
.stats-ask-form textarea:disabled { opacity: 0.55; cursor: wait; }
.stats-ask-form button {
  padding: 14px 22px;
  border: 1px solid var(--royal-blue-bright);
  background: var(--royal-blue);
  color: var(--bone);
  font-family: var(--sans);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: 8px;
  cursor: pointer;
  transition: background 200ms;
  min-height: 56px;
}
.stats-ask-form button:hover:not(:disabled) { background: var(--royal-blue-bright); }
.stats-ask-form button:disabled { opacity: 0.45; cursor: not-allowed; }
.stats-ask-fineprint {
  color: var(--bone-dim);
  font-size: 0.625rem;
  letter-spacing: 0.12em;
  opacity: 0.7;
}

.stats-source {
  border-top: 1px solid var(--rule);
  background: rgba(3, 8, 16, 0.6);
  padding: 48px 0 64px;
}
.stats-source-inner p {
  max-width: 70ch;
  margin: 0;
  color: var(--bone-dim);
  font-family: var(--serif);
  font-size: 0.9375rem;
  line-height: 1.6;
}
.stats-source-inner a { color: var(--gold); text-decoration: underline; text-underline-offset: 3px; }
.stats-source-inner a:hover { color: var(--gold-bright); }

.stats-overview-byyear { margin-top: 28px; }
.stats-table tbody tr.stats-tr-total td {
  border-top: 2px solid var(--rule);
  background: rgba(0, 70, 135, 0.22);
  color: var(--bone);
  font-weight: 600;
}
.stats-table tbody tr.stats-tr-total td.stats-td-name { color: var(--gold); }
.stats-card.stats-card-total {
  border-color: var(--gold);
  background: rgba(0, 70, 135, 0.18);
}

@media (max-width: 980px) {
  .stats-hero-inner { grid-template-columns: 1fr; gap: 36px; }
  .stats-totals { grid-template-columns: repeat(4, 1fr); }
}
@media (max-width: 720px) {
  .stats-hero { padding: 44px 0 28px; }
  .stats-hero-inner,
  .stats-controls-inner,
  .stats-results-inner,
  .stats-source-inner { padding: 0 20px; }
  .stats-totals { grid-template-columns: repeat(2, 1fr); }
  .stats-controls { padding: 12px 0; top: 60px; }
  .stats-controls-inner { grid-template-columns: 1fr; gap: 12px; }
  .stats-tabs { width: 100%; justify-content: center; }
  .stats-tab { flex: 1; justify-content: center; padding: 10px 8px; font-size: 0.8125rem; }
  .stats-tab-count { display: none; }

  /* Switch from table to card layout on small screens */
  .stats-table-wrap { display: none; }
  .stats-cards {
    display: grid;
    gap: 10px;
  }
  .stats-card {
    padding: 14px 16px;
    border: 1px solid var(--rule);
    background: rgba(3, 8, 16, 0.55);
  }
  .stats-card-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }
  .stats-card-title {
    font-family: var(--serif);
    font-size: 1.125rem;
    color: var(--bone);
    line-height: 1.1;
  }
  .stats-card-tag {
    flex-shrink: 0;
    padding: 2px 8px;
    border: 1px solid var(--rule);
    border-radius: 999px;
    color: var(--gold);
    font-size: 0.625rem;
    letter-spacing: 0.12em;
  }
  .stats-card-line {
    color: var(--bone-dim);
    font-size: 0.75rem;
    margin-bottom: 6px;
  }
  .stats-card-slash {
    color: var(--bone);
    font-size: 0.8125rem;
  }
  .stats-split-card dl { grid-template-columns: repeat(3, 1fr); }
  .stats-ask-form { grid-template-columns: 1fr; }
  .stats-ask-form button { width: 100%; }
}
@media (max-width: 420px) {
  .stats-title { font-size: 2.25rem; }
}
`;
document.head.appendChild(statsStyles);
